// Mock data for the admin dashboard

// User types and interfaces
export interface User {
  id: string;
  name: string;
  email: string;
  role: 'Student' | 'Faculty' | 'Staff' | 'Librarian';
  status: 'Active' | 'Inactive' | 'Suspended';
  joined: string;
  lastLogin?: string;
  phone?: string;
  department?: string;
  booksCheckedOut: number;
  overdueBooks: number;
  totalBorrowed: number;
  avatar?: string;
}

// Book types and interfaces
export interface Book {
  id: string;
  title: string;
  authors: string[];
  isbn: string;
  category: string;
  subcategory?: string;
  genre: string[];
  publisher: string;
  publicationYear: number;
  totalCopies: number;
  availableCopies: number;
  checkedOutCopies: number;
  reservedCopies: number;
  description: string;
  coverImage?: string;
  language: string;
  pages?: number;
  location: string;
  addedDate: string;
  lastUpdated: string;
  popularity: number;
  rating?: number;
}

// Loan types and interfaces
export interface Loan {
  id: string;
  bookId: string;
  bookTitle: string;
  userId: string;
  userName: string;
  userEmail: string;
  checkoutDate: string;
  dueDate: string;
  returnDate?: string;
  status: 'Active' | 'Overdue' | 'Returned' | 'Lost' | 'Damaged';
  renewalCount: number;
  maxRenewals: number;
  fineAmount?: number;
  notes?: string;
}

// Notification types
export interface Notification {
  id: number;
  type: 'overdue' | 'system' | 'inventory' | 'user' | 'general';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high';
  isRead: boolean;
  createdAt: string;
  action?: string;
}

// Library Settings Interface
export interface LibrarySettings {
  general: {
    name: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    timezone: string;
  };
  hours: {
    [key: string]: {
      open: string;
      close: string;
      closed: boolean;
    };
  };
  policies: {
    maxBooksPerUser: {
      [role: string]: number;
    };
    loanPeriodDays: {
      [role: string]: number;
    };
    maxRenewals: {
      [role: string]: number;
    };
    finePerDay: number;
    maxFineAmount: number;
  };
}

// Users
export const mockUsers: User[] = [
  {
    id: '1',
    name: 'Emma Wilson',
    email: 'emma.wilson@student.smchs.edu',
    role: 'Student',
    status: 'Active',
    joined: '2023-09-01',
    lastLogin: '2024-01-15',
    phone: '+1-555-0123',
    department: 'English Literature',
    booksCheckedOut: 3,
    overdueBooks: 1,
    totalBorrowed: 28,
    avatar: '/avatars/emma.jpg'
  },
  {
    id: '2',
    name: 'Dr. Michael Chen',
    email: 'michael.chen@faculty.smchs.edu',
    role: 'Faculty',
    status: 'Active',
    joined: '2022-08-15',
    lastLogin: '2024-01-14',
    phone: '+1-555-0456',
    department: 'Computer Science',
    booksCheckedOut: 5,
    overdueBooks: 0,
    totalBorrowed: 45,
    avatar: '/avatars/michael.jpg'
  },
  {
    id: '3',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@student.smchs.edu',
    role: 'Student',
    status: 'Active',
    joined: '2023-09-01',
    lastLogin: '2024-01-13',
    phone: '+1-555-0789',
    department: 'History',
    booksCheckedOut: 2,
    overdueBooks: 0,
    totalBorrowed: 22,
    avatar: '/avatars/sarah.jpg'
  },
  {
    id: '4',
    name: 'David Rodriguez',
    email: 'david.rodriguez@student.smchs.edu',
    role: 'Student',
    status: 'Suspended',
    joined: '2023-09-01',
    lastLogin: '2024-01-10',
    phone: '+1-555-0321',
    department: 'Mathematics',
    booksCheckedOut: 0,
    overdueBooks: 2,
    totalBorrowed: 15,
    avatar: '/avatars/david.jpg'
  },
  {
    id: '5',
    name: 'Jennifer Lee',
    email: 'jennifer.lee@staff.smchs.edu',
    role: 'Librarian',
    status: 'Active',
    joined: '2021-07-01',
    lastLogin: '2024-01-15',
    phone: '+1-555-0654',
    department: 'Library Services',
    booksCheckedOut: 8,
    overdueBooks: 0,
    totalBorrowed: 120,
    avatar: '/avatars/jennifer.jpg'
  }
];

// Books
export const mockBooks: Book[] = [
  {
    id: '1',
    title: 'To Kill a Mockingbird',
    authors: ['Harper Lee'],
    isbn: '9780060935467',
    category: 'Fiction',
    subcategory: 'Classic Literature',
    genre: ['Drama', 'Social Issues'],
    publisher: 'Harper Perennial Modern Classics',
    publicationYear: 1960,
    totalCopies: 15,
    availableCopies: 12,
    checkedOutCopies: 3,
    reservedCopies: 0,
    description: 'A gripping, heart-wrenching, and wholly remarkable tale of coming-of-age in a South poisoned by virulent prejudice.',
    coverImage: '/covers/mockingbird.jpg',
    language: 'English',
    pages: 376,
    location: 'Fiction Section - A1',
    addedDate: '2023-01-15',
    lastUpdated: '2024-01-10',
    popularity: 85,
    rating: 4.8
  },
  {
    id: '2',
    title: '1984',
    authors: ['George Orwell'],
    isbn: '9780451524935',
    category: 'Fiction',
    subcategory: 'Dystopian',
    genre: ['Science Fiction', 'Political Fiction'],
    publisher: 'Signet Classics',
    publicationYear: 1949,
    totalCopies: 20,
    availableCopies: 15,
    checkedOutCopies: 5,
    reservedCopies: 2,
    description: 'A dystopian social science fiction novel that follows the life of Winston Smith.',
    coverImage: '/covers/1984.jpg',
    language: 'English',
    pages: 328,
    location: 'Fiction Section - A2',
    addedDate: '2023-01-15',
    lastUpdated: '2024-01-12',
    popularity: 92,
    rating: 4.9
  },
  {
    id: '3',
    title: 'The Great Gatsby',
    authors: ['F. Scott Fitzgerald'],
    isbn: '9780743273565',
    category: 'Fiction',
    subcategory: 'Classic Literature',
    genre: ['Drama', 'Romance'],
    publisher: 'Scribner',
    publicationYear: 1925,
    totalCopies: 18,
    availableCopies: 16,
    checkedOutCopies: 2,
    reservedCopies: 1,
    description: 'The story of the mysteriously wealthy Jay Gatsby and his love for the beautiful Daisy Buchanan.',
    coverImage: '/covers/gatsby.jpg',
    language: 'English',
    pages: 180,
    location: 'Fiction Section - A3',
    addedDate: '2023-01-18',
    lastUpdated: '2024-01-08',
    popularity: 78,
    rating: 4.6
  },
  {
    id: '4',
    title: 'Introduction to Computer Science',
    authors: ['Dr. Jane Smith', 'Prof. Robert Brown'],
    isbn: '9781234567890',
    category: 'Textbooks',
    subcategory: 'Computer Science',
    genre: ['Educational', 'Technology'],
    publisher: 'Academic Press',
    publicationYear: 2023,
    totalCopies: 25,
    availableCopies: 20,
    checkedOutCopies: 5,
    reservedCopies: 3,
    description: 'An introduction to computer science and programming.',
    coverImage: '/covers/intro-cs.jpg',
    language: 'English',
    pages: 645,
    location: 'Textbooks - T1',
    addedDate: '2023-08-01',
    lastUpdated: '2024-01-05',
    popularity: 95,
    rating: 4.7
  },
  {
    id: '5',
    title: 'The Hobbit',
    authors: ['J.R.R. Tolkien'],
    isbn: '9780547928227',
    category: 'Fiction',
    subcategory: 'Fantasy',
    genre: ['Fantasy', 'Adventure'],
    publisher: 'Houghton Mifflin Harcourt',
    publicationYear: 1937,
    totalCopies: 12,
    availableCopies: 8,
    checkedOutCopies: 4,
    reservedCopies: 1,
    description: 'The enchanting prelude to The Lord of the Rings, a classic tale of adventure.',
    coverImage: '/covers/hobbit.jpg',
    language: 'English',
    pages: 366,
    location: 'Fiction Section - F1',
    addedDate: '2023-01-22',
    lastUpdated: '2024-01-14',
    popularity: 88,
    rating: 4.8
  }
];

// Loans
export const mockLoans: Loan[] = [
  {
    id: 'L001',
    bookId: '1',
    bookTitle: 'To Kill a Mockingbird',
    userId: '1',
    userName: 'Emma Wilson',
    userEmail: 'emma.wilson@student.smchs.edu',
    checkoutDate: '2024-01-01',
    dueDate: '2024-01-15',
    status: 'Overdue',
    renewalCount: 1,
    maxRenewals: 2,
    fineAmount: 5.50,
    notes: 'Student notified about overdue status'
  },
  {
    id: 'L002',
    bookId: '2',
    bookTitle: '1984',
    userId: '2',
    userName: 'Dr. Michael Chen',
    userEmail: 'michael.chen@faculty.smchs.edu',
    checkoutDate: '2024-01-05',
    dueDate: '2024-02-05',
    status: 'Active',
    renewalCount: 0,
    maxRenewals: 3,
    notes: 'Faculty extended loan period'
  },
  {
    id: 'L003',
    bookId: '3',
    bookTitle: 'The Great Gatsby',
    userId: '3',
    userName: 'Sarah Johnson',
    userEmail: 'sarah.johnson@student.smchs.edu',
    checkoutDate: '2023-12-20',
    dueDate: '2024-01-03',
    returnDate: '2024-01-02',
    status: 'Returned',
    renewalCount: 0,
    maxRenewals: 2,
    notes: 'Returned early'
  },
  {
    id: 'L004',
    bookId: '4',
    bookTitle: 'Introduction to Computer Science',
    userId: '2',
    userName: 'Dr. Michael Chen',
    userEmail: 'michael.chen@faculty.smchs.edu',
    checkoutDate: '2024-01-10',
    dueDate: '2024-04-10',
    status: 'Active',
    renewalCount: 0,
    maxRenewals: 3,
    notes: 'Semester-long textbook loan'
  },
  {
    id: 'L005',
    bookId: '5',
    bookTitle: 'The Hobbit',
    userId: '5',
    userName: 'Jennifer Lee',
    userEmail: 'jennifer.lee@staff.smchs.edu',
    checkoutDate: '2024-01-12',
    dueDate: '2024-02-12',
    status: 'Active',
    renewalCount: 0,
    maxRenewals: 3,
    notes: 'Staff borrowing'
  }
];

// Notification data
export const mockNotifications: Notification[] = [
  {
    id: 1,
    type: 'overdue',
    title: 'Overdue Books Alert',
    message: '15 books are now overdue. Send reminder emails to users.',
    priority: 'high',
    isRead: false,
    createdAt: '2024-12-20T10:30:00Z',
    action: 'Send Reminders'
  },
  {
    id: 2,
    type: 'system',
    title: 'System Maintenance Scheduled',
    message: 'Scheduled maintenance window on Dec 22, 2024 from 2:00 AM - 4:00 AM',
    priority: 'medium',
    isRead: false,
    createdAt: '2024-12-20T09:15:00Z',
    action: 'View Details'
  },
  {
    id: 3,
    type: 'inventory',
    title: 'Low Stock Alert',
    message: '8 popular books have only 1 copy remaining in inventory.',
    priority: 'medium',
    isRead: true,
    createdAt: '2024-12-20T08:45:00Z',
    action: 'Order More'
  },
  {
    id: 4,
    type: 'user',
    title: 'New Registration Pending',
    message: '5 new user registrations require approval.',
    priority: 'low',
    isRead: true,
    createdAt: '2024-12-19T16:20:00Z',
    action: 'Review Users'
  },
  {
    id: 5,
    type: 'overdue',
    title: 'Fine Collection Update',
    message: 'Total outstanding fines: $245.50 from 12 users.',
    priority: 'medium',
    isRead: false,
    createdAt: '2024-12-19T14:10:00Z',
    action: 'View Report'
  }
];

// Statistics data
export const mockStats = {
  overview: {
    totalBooks: 1247,
    totalUsers: 456,
    activeLoans: 234,
    overdueBooks: 12,
    monthlyGrowth: {
      books: 5.2,
      users: 12.3,
      loans: 8.7,
      returns: 15.4
    }
  },
  books: {
    byCategory: [
      { category: 'Fiction', count: 542 },
      { category: 'Textbooks', count: 328 },
      { category: 'Reference', count: 189 },
      { category: 'Science', count: 156 },
      { category: 'History', count: 132 }
    ],
    popular: [
      { title: '1984', borrowCount: 45 },
      { title: 'To Kill a Mockingbird', borrowCount: 42 },
      { title: 'The Great Gatsby', borrowCount: 38 },
      { title: 'Introduction to Computer Science', borrowCount: 35 },
      { title: 'The Hobbit', borrowCount: 32 }
    ]
  },
  users: {
    byRole: [
      { role: 'Student', count: 389 },
      { role: 'Faculty', count: 45 },
      { role: 'Staff', count: 18 },
      { role: 'Librarian', count: 4 }
    ],
    byStatus: [
      { status: 'Active', count: 423 },
      { status: 'Inactive', count: 28 },
      { status: 'Suspended', count: 5 }
    ]
  },
  loans: {
    byStatus: [
      { status: 'Active', count: 234 },
      { status: 'Overdue', count: 12 },
      { status: 'Returned', count: 1456 },
      { status: 'Lost', count: 3 }
    ],
    monthlyTrends: [
      { month: 'Aug', borrowed: 245, returned: 218 },
      { month: 'Sep', borrowed: 312, returned: 298 },
      { month: 'Oct', borrowed: 278, returned: 289 },
      { month: 'Nov', borrowed: 234, returned: 267 },
      { month: 'Dec', borrowed: 189, returned: 201 },
      { month: 'Jan', borrowed: 156, returned: 142 }
    ]
  }
};

// Library settings
export const mockLibrarySettings = {
  general: {
    name: 'SMCHS Library',
    address: '123 Education Drive, Learning City, LC 12345',
    phone: '+1-555-0199',
    email: 'library@smchs.edu',
    website: 'https://library.smchs.edu',
    timezone: 'America/New_York'
  },
  hours: {
    monday: { open: '08:00', close: '18:00', closed: false },
    tuesday: { open: '08:00', close: '18:00', closed: false },
    wednesday: { open: '08:00', close: '18:00', closed: false },
    thursday: { open: '08:00', close: '18:00', closed: false },
    friday: { open: '08:00', close: '16:00', closed: false },
    saturday: { open: '10:00', close: '14:00', closed: false },
    sunday: { open: '12:00', close: '16:00', closed: false }
  },
  policies: {
    maxBooksPerUser: {
      Student: 5,
      Faculty: 10,
      Staff: 8,
      Librarian: 15
    },
    loanPeriodDays: {
      Student: 14,
      Faculty: 30,
      Staff: 21,
      Librarian: 30
    },
    maxRenewals: {
      Student: 2,
      Faculty: 3,
      Staff: 2,
      Librarian: 3
    },
    finePerDay: 0.50,
    maxFineAmount: 25.00
  }
};
